import { useEffect, useRef, useState } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import { Image as ImageIcon, Upload, Wand2, Copy, Trash2, Plus, X, Eye } from "lucide-react";

const POSITIONS = [
  { key: "left", label: "Left" },
  { key: "center", label: "Center" },
  { key: "right", label: "Right" },
];

const LOGO_POSITIONS = [
  { key: "top-left", label: "↖" },
  { key: "top-center", label: "↑" },
  { key: "top-right", label: "↗" },
  { key: "middle-left", label: "←" },
  { key: "middle-center", label: "•" },
  { key: "middle-right", label: "→" },
  { key: "bottom-left", label: "↙" },
  { key: "bottom-center", label: "↓" },
  { key: "bottom-right", label: "↘" },
];

const defaultForm = () => ({
  name: "",
  title: "",
  subtitle: "",
  text_color: "#ffffff",
  text_position: "left",
  text_align_v: "center",
  overlay_color: "#0d4f8a",
  overlay_opacity: 0.45,
  logo_url: "",
  logo_position: "middle-right",
  logo_size_pct: 45,
  height: 220,
  title_size: 56,
  subtitle_size: 28,
});

function ColorInput({ value, onChange, testId }) {
  return (
    <div className="flex items-center gap-2 w-full">
      <input type="color" value={value} onChange={e => onChange(e.target.value)} className="w-9 h-9 rounded border border-white/10 bg-transparent shrink-0" data-testid={`${testId}-color`} />
      <input type="text" value={value} onChange={e => onChange(e.target.value)} className="input flex-1" data-testid={`${testId}-hex`} />
    </div>
  );
}

export default function Banners() {
  const [banners, setBanners] = useState([]);
  const [form, setForm] = useState(defaultForm());
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [settings, setSettings] = useState(null);
  const fileRef = useRef(null);

  const refresh = async () => {
    const [b, s] = await Promise.all([api.get("/banners"), api.get("/settings")]);
    setBanners(b.data);
    setSettings(s.data);
    if (!form.logo_url && s.data?.logoUrl) {
      setForm(f => ({ ...f, logo_url: s.data.logoUrl }));
    }
  };
  useEffect(() => { refresh(); }, []);

  const update = (patch) => setForm(f => ({ ...f, ...patch }));

  const pickFile = (f) => {
    if (!f) return;
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => setFilePreview(e.target.result);
    reader.readAsDataURL(f);
  };

  const previewBanner = async () => {
    if (!file) { toast.error("Upload a base image first"); return; }
    setPreviewing(true);
    try {
      const data = new FormData();
      data.append("image", file);
      Object.entries(form).forEach(([k, v]) => { if (k !== "name") data.append(k, String(v)); });
      const r = await api.post("/banners/preview", data, {
        headers: { "Content-Type": "multipart/form-data" },
        responseType: "blob",
      });
      // revoke previous URL to avoid memory leaks
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(r.data));
    } catch (e) {
      toast.error("Preview failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setPreviewing(false);
    }
  };

  const createBanner = async () => {
    if (!file) { toast.error("Upload a base image first"); return; }
    if (!form.name.trim()) { toast.error("Give the banner a name"); return; }
    setSaving(true);
    try {
      const data = new FormData();
      data.append("image", file);
      Object.entries(form).forEach(([k, v]) => data.append(k, String(v)));
      const r = await api.post("/banners", data, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Banner created");
      navigator.clipboard.writeText(r.data.url).catch(() => {});
      toast.info("URL copied to clipboard");
      setFile(null); setFilePreview(null);
      if (fileRef.current) fileRef.current.value = "";
      refresh();
    } catch (e) {
      toast.error("Create failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete banner "${name}"?`)) return;
    await api.delete(`/banners/${id}`);
    refresh();
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("URL copied — paste into Designer → Banner image URL");
  };

  return (
    <div className="p-8 max-w-[1500px]">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-emerald-400 text-xs uppercase tracking-widest mb-1">
          <ImageIcon size={14} /> Studio
        </div>
        <h1 className="text-3xl font-bold tracking-tight">Banner Studio</h1>
        <p className="text-slate-400 mt-1 text-sm max-w-2xl">
          Upload a photo, add seasonal text + your logo, and we&apos;ll generate a polished PNG banner. Paste the URL into any signature&apos;s Banner section. Update anytime — Outlook fetches the latest version automatically.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-4">
        {/* LEFT: form */}
        <div className="glass p-5 max-h-[80vh] overflow-y-auto">
          <div className="mb-4">
            <div className="label">Banner name</div>
            <input className="input" value={form.name} onChange={e => update({ name: e.target.value })} placeholder="e.g. Spring 2026 promo" data-testid="banner-name" />
          </div>

          <div className="mb-4">
            <div className="label">Base image</div>
            <label className="block border-2 border-dashed border-white/10 rounded-lg p-6 text-center hover:border-emerald-500/30 cursor-pointer transition-colors" data-testid="upload-area">
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => pickFile(e.target.files?.[0])} data-testid="upload-input" />
              {filePreview ? (
                <div>
                  <img src={filePreview} alt="" className="max-h-32 mx-auto rounded" />
                  <div className="text-xs text-slate-400 mt-2">Click to replace</div>
                </div>
              ) : (
                <>
                  <Upload className="mx-auto text-slate-500" size={28} />
                  <div className="text-sm text-slate-300 mt-2">Drop or click to upload</div>
                  <div className="text-[11px] text-slate-500">JPG/PNG/WebP, up to 12MB. Wider images work best (3:1 ratio).</div>
                </>
              )}
            </label>
          </div>

          <div className="mb-4 grid grid-cols-1 gap-2">
            <div>
              <div className="label">Headline</div>
              <input className="input" value={form.title} onChange={e => update({ title: e.target.value })} placeholder="e.g. Boiler servicing — book before October" data-testid="banner-title" />
            </div>
            <div>
              <div className="label">Sub-line (optional)</div>
              <input className="input" value={form.subtitle} onChange={e => update({ subtitle: e.target.value })} placeholder="e.g. Save 15% with code SPRING25" data-testid="banner-subtitle" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 mb-4">
            <div>
              <div className="label">Text colour</div>
              <ColorInput value={form.text_color} onChange={v => update({ text_color: v })} testId="text-color" />
            </div>
            <div>
              <div className="label">Tint / overlay</div>
              <ColorInput value={form.overlay_color} onChange={v => update({ overlay_color: v })} testId="overlay-color" />
            </div>
          </div>

          <div className="mb-4">
            <div className="label">Overlay strength ({Math.round(form.overlay_opacity * 100)}%)</div>
            <input type="range" min={0} max={1} step={0.05} value={form.overlay_opacity} onChange={e => update({ overlay_opacity: parseFloat(e.target.value) })} className="w-full accent-emerald-500" data-testid="overlay-opacity" />
          </div>

          <div className="grid grid-cols-2 gap-2 mb-4">
            <div>
              <div className="label">Text position</div>
              <select className="select" value={form.text_position} onChange={e => update({ text_position: e.target.value })} data-testid="text-position">
                {POSITIONS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <div className="label">Vertical</div>
              <select className="select" value={form.text_align_v} onChange={e => update({ text_align_v: e.target.value })} data-testid="text-align-v">
                <option value="top">Top</option>
                <option value="center">Middle</option>
                <option value="bottom">Bottom</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 mb-4">
            <div>
              <div className="label">Headline size (px)</div>
              <input type="number" min={20} max={120} className="input" value={form.title_size} onChange={e => update({ title_size: parseInt(e.target.value || 56) })} data-testid="title-size" />
            </div>
            <div>
              <div className="label">Sub-line size (px)</div>
              <input type="number" min={12} max={80} className="input" value={form.subtitle_size} onChange={e => update({ subtitle_size: parseInt(e.target.value || 28) })} data-testid="subtitle-size" />
            </div>
          </div>

          <div className="mb-4 border-t border-white/5 pt-4">
            <div className="label">Logo URL</div>
            <input className="input" value={form.logo_url} onChange={e => update({ logo_url: e.target.value })} placeholder="https://... (your brand logo)" data-testid="logo-url" />
            <div className="text-[11px] text-slate-500 mt-1">Uses your Brand Settings logo by default. Leave blank for no logo.</div>
          </div>

          <div className="mb-4">
            <div className="label">Logo size ({form.logo_size_pct}% of banner height)</div>
            <input type="range" min={15} max={85} step={1} value={form.logo_size_pct} onChange={e => update({ logo_size_pct: parseInt(e.target.value) })} className="w-full accent-emerald-500" data-testid="logo-size" />
          </div>

          <div className="mb-4">
            <div className="label">Logo position</div>
            <div className="grid grid-cols-3 gap-1.5 max-w-[200px]">
              {LOGO_POSITIONS.map(p => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => update({ logo_position: p.key })}
                  data-testid={`logo-pos-${p.key}`}
                  className={`h-10 rounded border text-lg transition-colors ${form.logo_position === p.key ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-300" : "border-white/10 bg-white/[0.03] text-slate-400 hover:bg-white/[0.06]"}`}
                  title={p.key}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => update({ logo_position: "none" })}
              data-testid="logo-pos-none"
              className={`btn mt-2 text-xs ${form.logo_position === "none" ? "btn-primary" : ""}`}
            >
              No logo
            </button>
          </div>

          <div className="mb-4">
            <div className="label">Banner height (px)</div>
            <input type="number" min={120} max={400} className="input" value={form.height} onChange={e => update({ height: parseInt(e.target.value || 220) })} data-testid="banner-height" />
          </div>

          <div className="pt-4 border-t border-white/5 grid grid-cols-2 gap-2">
            <button onClick={previewBanner} disabled={previewing || !file} className="btn justify-center" data-testid="preview-banner">
              <Eye size={14} /> {previewing ? "Composing…" : "Preview"}
            </button>
            <button onClick={createBanner} disabled={saving || !file} className="btn btn-primary justify-center" data-testid="create-banner">
              <Wand2 size={14} /> {saving ? "Saving…" : "Save banner"}
            </button>
          </div>
        </div>

        {/* RIGHT: preview + saved banners */}
        <div className="space-y-4">
          {/* Preview pane */}
          <div className="glass p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold">Preview</div>
              {previewUrl && (
                <button onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }} className="btn text-xs !py-1 !px-2" data-testid="clear-preview">
                  <X size={12} /> Clear
                </button>
              )}
            </div>
            {previewUrl ? (
              <img src={previewUrl} alt="preview" className="w-full rounded border border-white/10" data-testid="preview-image" />
            ) : (
              <div className="border border-dashed border-white/10 rounded-lg p-8 text-center text-sm text-slate-500" data-testid="preview-placeholder">
                {file ? "Click Preview to see the composed banner before saving." : "Upload a base image to enable preview."}
              </div>
            )}
          </div>

          {/* Saved banners */}
          <div className="glass p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold">Saved Banners ({banners.length})</div>
            <div className="text-[11px] text-slate-500">Click a URL to copy → paste into Designer</div>
          </div>
          {banners.length === 0 ? (
            <div className="text-slate-500 text-sm p-8 text-center border border-dashed border-white/10 rounded-lg">
              No banners yet. Upload an image on the left and click &quot;Save banner&quot;.
            </div>
          ) : (
            <div className="space-y-3">
              {banners.map(b => (
                <div key={b.id} className="border border-white/10 bg-white/[0.02] rounded-lg p-3" data-testid={`banner-${b.id}`}>
                  <div className="flex gap-3 items-start">
                    <img src={b.url} alt={b.name} className="w-56 h-auto rounded border border-white/10 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-100">{b.name}</div>
                      <div className="text-xs text-slate-400 mt-1">{b.params.title}{b.params.subtitle ? ` · ${b.params.subtitle}` : ""}</div>
                      <div className="text-[10px] text-slate-500 mt-1">{new Date(b.created_at).toLocaleString()}</div>
                      <div className="mt-2 flex items-center gap-1.5">
                        <button onClick={() => copy(b.url)} className="btn !py-1 !px-2 text-[11px]" data-testid={`copy-${b.id}`}>
                          <Copy size={11} /> Copy URL
                        </button>
                        <a href={b.url} target="_blank" rel="noopener noreferrer" className="btn !py-1 !px-2 text-[11px]">Open</a>
                        <button onClick={() => remove(b.id, b.name)} className="btn btn-danger !py-1 !px-2 text-[11px] ml-auto" data-testid={`remove-${b.id}`}>
                          <Trash2 size={11} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}
